import { useState } from 'react'
import { useSearch, useNavigate } from '@tanstack/react-router'
import { Package, Calendar, Play, AlertCircle, CheckCircle, Clock, Activity, ChevronDown, ChevronRight, Layers, FlaskConical, Pencil, X, Check } from 'lucide-react'
import { useInventory, useSetTrialOverride } from '../hooks/useInventory'
import { useApiToken } from '../hooks/useApiToken'
import { inventoryApi } from '../lib/api/inventory'

export default function Inventory() {
  const { filter = 'all' } = useSearch({ strict: false })
  const navigate = useNavigate()
  const [showStale, setShowStale] = useState(false)
  const [customerFilter, setCustomerFilter] = useState('all')
  const [workspaceFilter, setWorkspaceFilter] = useState('all')
  const [itemTypeFilter, setItemTypeFilter] = useState('all')
  const [groupBy, setGroupBy] = useState('tree')
  const [expandedItems, setExpandedItems] = useState({})
  const [compositions, setCompositions] = useState({})
  const [expandedTrialCustomers, setExpandedTrialCustomers] = useState(new Set())
  const [expandedTreeCustomers, setExpandedTreeCustomers] = useState(new Set())
  const [editingOverride, setEditingOverride] = useState(null)
  const [overrideDate, setOverrideDate] = useState('')
  const [overrideError, setOverrideError] = useState(null)

  const { data: inventory = [], isLoading } = useInventory()
  const setTrialOverride = useSetTrialOverride()
  const getToken = useApiToken()

  const setFilter = (value) => navigate({ to: '/inventory', search: { filter: value }, replace: true })

  const handleCustomerFilterChange = (value) => {
    setCustomerFilter(value)
    setWorkspaceFilter('all')
  }

  const loadPipelineComposition = async (pipelineName) => {
    setCompositions(prev => ({ ...prev, [pipelineName]: { loading: true } }))
    try {
      const token = await getToken()
      const data = await inventoryApi.getPipelineComposition(token, pipelineName)
      setCompositions(prev => ({ ...prev, [pipelineName]: data }))
    } catch {
      setCompositions(prev => ({ ...prev, [pipelineName]: { error: true } }))
    }
  }

  const toggleExpand = (itemName, itemType) => {
    const isCurrentlyExpanded = expandedItems[itemName]
    setExpandedItems(prev => ({ ...prev, [itemName]: !isCurrentlyExpanded }))
    if (!isCurrentlyExpanded && itemType === 'datapipeline' && !compositions[itemName]) {
      loadPipelineComposition(itemName)
    }
  }

  const isOverdue = (item) => {
    if (!item.isScheduled) return false
    if (!item.lastExecution) return true
    const thresholdMs = item.scheduleFrequency === 'hourly'  ? 2   * 60 * 60 * 1000
      : item.scheduleFrequency === 'daily'   ? 26  * 60 * 60 * 1000
      : item.scheduleFrequency === 'weekly'  ? 170 * 60 * 60 * 1000
      : 2 * 24 * 60 * 60 * 1000
    return new Date(item.lastExecution) < new Date(Date.now() - thresholdMs)
  }

  const saveTrialOverride = async (ws) => {
    setOverrideError(null)
    try {
      await setTrialOverride.mutateAsync({
        capacityId: ws.capacityId,
        tenantId: ws.tenantId,
        overrideDate: overrideDate || null,
      })
      setEditingOverride(null)
    } catch (e) {
      setOverrideError('Failed to save — ' + e.message)
    }
  }

  const clearTrialOverride = (ws) => {
    setTrialOverride.mutate({ capacityId: ws.capacityId, tenantId: ws.tenantId, overrideDate: null })
  }

  const staleCount = inventory.filter(item => item.isStale).length

  const filteredInventory = inventory.filter(item => {
    if (item.isStale && !showStale) return false
    if (filter === 'scheduled' && !item.isScheduled) return false
    if (filter === 'monitored' && !item.hasMonitoring) return false
    if (filter === 'gaps' && !(item.isScheduled && !item.hasMonitoring)) return false
    if (filter === 'overdue' && !isOverdue(item)) return false
    if (filter === 'trial' && !item.capacityIsTrial) return false
    if (customerFilter !== 'all' && item.customerName !== customerFilter) return false
    if (workspaceFilter !== 'all' && item.workspaceName !== workspaceFilter) return false
    if (itemTypeFilter !== 'all' && item.itemType !== itemTypeFilter) return false
    return true
  })

  const trialWorkspaces = Object.values(
    filteredInventory
      .filter(item => item.capacityIsTrial)
      .reduce((acc, item) => {
        if (!acc[item.workspaceId]) {
          acc[item.workspaceId] = {
            workspaceId: item.workspaceId,
            workspaceName: item.workspaceName,
            customerName: item.customerName,
            tenantId: item.tenantId,
            capacityId: item.capacityId,
            capacitySku: item.capacitySku,
            capacityTrialExpiresAt: item.capacityTrialExpiresAt,
            capacityTrialExpiresAtOverride: item.capacityTrialExpiresAtOverride
          }
        }
        return acc
      }, {})
  ).sort((a, b) => {
    if (!a.capacityTrialExpiresAt) return 1
    if (!b.capacityTrialExpiresAt) return -1
    return new Date(a.capacityTrialExpiresAt) - new Date(b.capacityTrialExpiresAt)
  })

  const customers = [...new Set(inventory.map(item => item.customerName))].sort()
  const workspaces = [...new Set(
    inventory
      .filter(item => customerFilter === 'all' || item.customerName === customerFilter)
      .map(item => item.workspaceName)
  )].sort()

  const groupedInventory = () => {
    if (groupBy === 'customer') {
      return filteredInventory.reduce((groups, item) => {
        const key = item.customerName
        if (!groups[key]) groups[key] = []
        groups[key].push(item)
        return groups
      }, {})
    } else {
      return filteredInventory.reduce((groups, item) => {
        const key = `${item.customerName} - ${item.workspaceName}`
        if (!groups[key]) groups[key] = []
        groups[key].push(item)
        return groups
      }, {})
    }
  }

  const treeInventory = () => {
    return filteredInventory.reduce((tree, item) => {
      const customer = item.customerName
      const capacityKey = item.capacityId || '__none__'
      const wsKey = item.workspaceId
      if (!tree[customer]) tree[customer] = {}
      if (!tree[customer][capacityKey]) {
        tree[customer][capacityKey] = {
          capacityId: item.capacityId,
          capacitySku: item.capacitySku,
          capacityIsTrial: item.capacityIsTrial,
          capacityTrialExpiresAt: item.capacityTrialExpiresAt,
          workspaces: {}
        }
      }
      if (!tree[customer][capacityKey].workspaces[wsKey]) {
        tree[customer][capacityKey].workspaces[wsKey] = { workspaceId: item.workspaceId, workspaceName: item.workspaceName, items: [] }
      }
      tree[customer][capacityKey].workspaces[wsKey].items.push(item)
      return tree
    }, {})
  }

  const stats = {
    total: inventory.length,
    scheduled: inventory.filter(i => i.isScheduled).length,
    monitored: inventory.filter(i => i.hasMonitoring).length,
    gaps: inventory.filter(i => i.isScheduled && !i.hasMonitoring).length
  }

  if (isLoading) {
    return <div className="loading">Loading inventory...</div>
  }

  return (
    <div className="inventory">
      <h1>Fabric Inventory</h1>
      <p className="subtitle">Discovered notebooks and pipelines across all customers</p>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><Package size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Total Items</p>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple"><Calendar size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Scheduled</p>
            <p className="stat-value">{stats.scheduled}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Monitored</p>
            <p className="stat-value">{stats.monitored}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon red"><AlertCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Coverage Gaps</p>
            <p className="stat-value">{stats.gaps}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="inventory-filters">
        <div className="filter-group">
          <label>Show:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Items</option>
            <option value="scheduled">Scheduled Only</option>
            <option value="monitored">Monitored Only</option>
            <option value="gaps">Coverage Gaps</option>
            <option value="overdue">⚠️ Overdue Scheduled</option>
            <option value="trial">🧪 Trial Workspaces</option>
          </select>
        </div>

        {staleCount > 0 && (
          <div className="filter-group">
            <button
              onClick={() => setShowStale(s => !s)}
              style={{ fontSize: '0.8rem', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', background: showStale ? 'var(--text-muted)' : 'var(--bg-card)', color: showStale ? 'white' : 'var(--text-muted)' }}
            >
              {showStale ? `Hide stale (${staleCount})` : `Show stale (${staleCount})`}
            </button>
          </div>
        )}

        <div className="filter-group">
          <label>Customer:</label>
          <select value={customerFilter} onChange={(e) => handleCustomerFilterChange(e.target.value)}>
            <option value="all">All Customers</option>
            {customers.map(customer => (
              <option key={customer} value={customer}>{customer}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Workspace:</label>
          <select value={workspaceFilter} onChange={(e) => setWorkspaceFilter(e.target.value)}>
            <option value="all">All Workspaces</option>
            {workspaces.map(ws => (
              <option key={ws} value={ws}>{ws}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select value={itemTypeFilter} onChange={(e) => setItemTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="notebook">Notebooks</option>
            <option value="datapipeline">Pipelines</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Group by:</label>
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
            <option value="customer">Customer</option>
            <option value="workspace">Workspace</option>
            <option value="tree">Tree view</option>
          </select>
        </div>
      </div>

      {/* Trial Workspace Summary */}
      {trialWorkspaces.length > 0 && (() => {
        const byCustomer = trialWorkspaces.reduce((acc, ws) => {
          if (!acc[ws.customerName]) acc[ws.customerName] = {}
          const capKey = ws.capacityId || '__none__'
          if (!acc[ws.customerName][capKey]) acc[ws.customerName][capKey] = { capacityId: ws.capacityId, capacitySku: ws.capacitySku, capacityTrialExpiresAt: ws.capacityTrialExpiresAt, workspaces: [] }
          acc[ws.customerName][capKey].workspaces.push(ws)
          return acc
        }, {})
        return (
          <div style={{ marginBottom: '1.5rem', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ padding: '0.75rem 1rem', background: '#fff8f0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FlaskConical size={16} style={{ color: 'var(--warning)' }} />
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--warning)' }}>
                Trial Capacities ({trialWorkspaces.length} workspace{trialWorkspaces.length !== 1 ? 's' : ''})
              </span>
            </div>
            {Object.entries(byCustomer).map(([customerName, capacities]) => {
              const isExpanded = expandedTrialCustomers.has(customerName)
              const toggleCustomer = () => setExpandedTrialCustomers(prev => {
                const next = new Set(prev)
                next.has(customerName) ? next.delete(customerName) : next.add(customerName)
                return next
              })
              const firstCap = Object.values(capacities)[0]
              const repWs = firstCap.workspaces[0]
              const totalWorkspaces = Object.values(capacities).reduce((n, c) => n + c.workspaces.length, 0)
              const effectiveExpiry = repWs?.capacityTrialExpiresAtOverride || firstCap.capacityTrialExpiresAt
              const expiry = effectiveExpiry ? formatTrialExpiry(effectiveExpiry) : 'unknown'
              const isExpired = expiry === 'expired'
              const isSoon = effectiveExpiry && (new Date(effectiveExpiry) - new Date()) < 7 * 86400000
              const hasOverride = !!repWs?.capacityTrialExpiresAtOverride
              const isEditing = editingOverride === customerName
              return (
                <div key={customerName}>
                  <div onClick={toggleCustomer} style={{ padding: '0.5rem 1rem', background: 'var(--gray-50)', borderBottom: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem' }}>
                    {isExpanded ? <ChevronDown size={14} style={{ flexShrink: 0 }} /> : <ChevronRight size={14} style={{ flexShrink: 0 }} />}
                    <span style={{ fontWeight: 600, color: 'var(--gray-700)' }}>{customerName}</span>
                    {firstCap.capacitySku && (
                      <span style={{ padding: '1px 8px', borderRadius: '8px', background: '#f3e5f5', color: '#7b1fa2', fontWeight: 600, fontSize: '0.75rem' }}>
                        {firstCap.capacitySku}
                      </span>
                    )}
                    <span style={{ color: isExpired ? 'var(--danger)' : isSoon ? 'var(--warning)' : 'var(--gray-500)', fontWeight: isSoon || isExpired ? 600 : 400, fontSize: '0.8rem' }}>
                      {expiry}
                    </span>
                    {hasOverride && !isEditing && (
                      <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>(overridden)</span>
                    )}
                    <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>
                      {totalWorkspaces} workspace{totalWorkspaces !== 1 ? 's' : ''}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: 'auto' }} onClick={e => e.stopPropagation()}>
                      {isEditing ? (
                        <>
                          <input type="date" value={overrideDate} onChange={e => { setOverrideDate(e.target.value); setOverrideError(null) }}
                            style={{ fontSize: '0.8rem', padding: '2px 6px', border: '1px solid var(--border)', borderRadius: '4px' }} />
                          <button onClick={() => saveTrialOverride(repWs)} disabled={setTrialOverride.isPending}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)', padding: '2px' }}>
                            <Check size={14} />
                          </button>
                          <button onClick={() => { setEditingOverride(null); setOverrideError(null) }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-500)', padding: '2px' }}>
                            <X size={14} />
                          </button>
                          {overrideError && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>{overrideError}</span>
                          )}
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setEditingOverride(customerName); setOverrideDate(repWs?.capacityTrialExpiresAtOverride ? repWs.capacityTrialExpiresAtOverride.substring(0, 10) : '') }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: '2px' }} title="Set manual expiry override">
                            <Pencil size={13} />
                          </button>
                          {hasOverride && (
                            <button onClick={() => clearTrialOverride(repWs)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '2px' }} title="Clear override">
                              <X size={13} />
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  </div>
                  {isExpanded && Object.values(capacities).flatMap(cap => cap.workspaces).map((ws) => (
                    <div key={ws.workspaceId} style={{ padding: '0.4rem 1rem 0.4rem 2.5rem', borderBottom: '1px solid var(--border)', fontSize: '0.83rem', color: 'var(--gray-700)' }}>
                      {ws.workspaceName}
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        )
      })()}

      {/* Inventory List */}
      <div className="inventory-list">
        {groupBy === 'tree' ? (
          Object.keys(treeInventory()).length === 0 ? (
            <div className="empty-state">
              <Package size={48} />
              <p>No items found matching the current filter</p>
            </div>
          ) : (
            Object.entries(treeInventory()).map(([customerName, capacities]) => {
              const isExpanded = expandedTreeCustomers.has(customerName)
              const toggleCustomer = () => setExpandedTreeCustomers(prev => {
                const next = new Set(prev)
                next.has(customerName) ? next.delete(customerName) : next.add(customerName)
                return next
              })
              const totalItems = Object.values(capacities).reduce((n, c) => n + Object.values(c.workspaces).reduce((m, w) => m + w.items.length, 0), 0)
              return (
                <div key={customerName} className="inventory-group">
                  <h3 className="group-header" onClick={toggleCustomer} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    {customerName}
                    <span className="group-count">({totalItems} items)</span>
                  </h3>
                  {isExpanded && Object.entries(capacities).map(([capacityKey, capacity]) => {
                    const expiry = capacity.capacityTrialExpiresAt ? formatTrialExpiry(capacity.capacityTrialExpiresAt) : null
                    const isSoon = capacity.capacityTrialExpiresAt && (new Date(capacity.capacityTrialExpiresAt) - new Date()) < 7 * 86400000
                    const isExpired = expiry === 'expired'
                    return (
                      <div key={capacityKey} style={{ marginBottom: '0.75rem', border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{ padding: '0.5rem 1rem', background: 'var(--gray-50)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <Layers size={14} style={{ color: 'var(--gray-500)' }} />
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {capacity.capacityId ? `Capacity: ${capacity.capacityId}` : 'No Capacity'}
                          </span>
                          {capacity.capacitySku && (
                            <span style={{ fontSize: '0.75rem', padding: '1px 8px', borderRadius: '8px', background: '#f3e5f5', color: '#7b1fa2', fontWeight: 600 }}>
                              {capacity.capacitySku}
                            </span>
                          )}
                          {capacity.capacityIsTrial && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: isExpired ? 'var(--danger)' : isSoon ? 'var(--warning)' : 'var(--gray-600)', fontWeight: isSoon || isExpired ? 600 : 400 }}>
                              <FlaskConical size={12} />
                              Trial {expiry ? `— ${expiry}` : ''}
                            </span>
                          )}
                        </div>
                        {Object.values(capacity.workspaces).map(workspace => (
                          <div key={workspace.workspaceId}>
                            <div style={{ padding: '0.4rem 1rem 0.4rem 2rem', background: '#fafafa', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontWeight: 500, fontSize: '0.83rem', color: 'var(--gray-700)' }}>{workspace.workspaceName}</span>
                              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>({workspace.items.length} items)</span>
                            </div>
                            <div className="inventory-items" style={{ paddingLeft: '1rem' }}>
                              {workspace.items.map((item, index) => (
                                <InventoryItem
                                  key={index}
                                  item={item}
                                  groupBy="workspace"
                                  isExpanded={expandedItems[item.itemName] || false}
                                  composition={compositions[item.itemName]}
                                  onToggleExpand={() => toggleExpand(item.itemName, item.itemType)}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              )
            })
          )
        ) : (
          Object.keys(groupedInventory()).length === 0 ? (
            <div className="empty-state">
              <Package size={48} />
              <p>No items found matching the current filter</p>
            </div>
          ) : (
            Object.entries(groupedInventory()).map(([groupName, items]) => (
              <div key={groupName} className="inventory-group">
                <h3 className="group-header">
                  {groupName}
                  <span className="group-count">({items.length} items)</span>
                </h3>
                <div className="inventory-items">
                  {items.map((item, index) => (
                    <InventoryItem
                      key={index}
                      item={item}
                      groupBy={groupBy}
                      isExpanded={expandedItems[item.itemName] || false}
                      composition={compositions[item.itemName]}
                      onToggleExpand={() => toggleExpand(item.itemName, item.itemType)}
                    />
                  ))}
                </div>
              </div>
            ))
          )
        )}
      </div>
    </div>
  )
}

function InventoryItem({ item, groupBy, isExpanded, composition, onToggleExpand }) {
  const isGap = item.isScheduled && !item.hasMonitoring
  const isPipeline = item.itemType === 'datapipeline'

  return (
    <div className={`inventory-item ${isGap ? 'gap' : ''} ${item.isStale ? 'stale' : ''} ${isExpanded ? 'expanded' : ''}`}>
      <div
        className={`item-header ${isPipeline ? 'clickable' : ''}`}
        onClick={isPipeline ? onToggleExpand : undefined}
        style={{ cursor: isPipeline ? 'pointer' : 'default' }}
      >
        {isPipeline && (
          <div className="expand-icon">
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        )}
        <div className="item-icon">
          {item.itemType === 'notebook' ? '📓' : '📊'}
        </div>
        <div className="item-title">
          <h4>{item.itemName}</h4>
          {groupBy === 'customer' && (
            <p className="item-workspace">{item.workspaceName}</p>
          )}
        </div>
      </div>

      <div className="item-details">
        <div className="item-badges">
          <span className={`badge ${item.itemType}`}>{item.itemType}</span>
          {item.isStale && (
            <span className="badge" style={{ background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb' }}>Stale</span>
          )}
          {item.isScheduled && (
            <span className="badge scheduled">
              <Calendar size={12} />
              {item.scheduleFrequency || 'Scheduled'}
            </span>
          )}
          {item.hasMonitoring ? (
            <span className="badge monitored"><CheckCircle size={12} />Monitored</span>
          ) : (
            <span className="badge not-monitored"><AlertCircle size={12} />No Monitoring</span>
          )}
        </div>

        <div className="item-metadata">
          {item.lastExecution && (
            <div className="metadata-row">
              <Activity size={14} />
              <span className="metadata-label">Last Run:</span>
              <span className="metadata-value">{formatDateTime(item.lastExecution)}</span>
            </div>
          )}
          {item.executionCount > 0 && (
            <div className="metadata-row">
              <Play size={14} />
              <span className="metadata-label">Executions (30d):</span>
              <span className="metadata-value">{item.executionCount}</span>
            </div>
          )}
          {item.isScheduled && item.scheduleDetails && (
            <div className="metadata-row">
              <Clock size={14} />
              <span className="metadata-label">Schedule:</span>
              <span className="metadata-value">{formatScheduleDetails(item.scheduleDetails)}</span>
            </div>
          )}
          {item.lastDiscoveredAt && (
            <div className="metadata-row">
              <Calendar size={14} />
              <span className="metadata-label">Discovered:</span>
              <span className="metadata-value">{formatDateTime(item.lastDiscoveredAt)}</span>
            </div>
          )}
        </div>
      </div>

      {isGap && (
        <div className="item-warning">⚠️ This item is scheduled but not monitored</div>
      )}

      {isPipeline && isExpanded && (
        <div className="pipeline-composition">
          {!composition || composition.loading ? (
            <div className="composition-loading">
              <div className="spinner"></div>
              <span>Loading pipeline composition...</span>
            </div>
          ) : composition.error ? (
            <div className="composition-loading"><span>⚠️ Could not load pipeline composition</span></div>
          ) : composition.error_detail ? (
            <div className="composition-loading">
              <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>⚠️ {composition.error_detail}</span>
            </div>
          ) : !composition.children?.length && !composition.composition && !composition.parentPipeline ? (
            <div className="composition-loading">
              <span style={{ color: 'var(--gray-500)' }}>No composition data available</span>
            </div>
          ) : (
            <>
              {composition.parentPipeline && (
                <div className="composition-section">
                  <div className="composition-header"><Layers size={16} /><span>Parent Pipeline</span></div>
                  <div className="composition-content">
                    <div className="parent-pipeline">👆 {composition.parentPipeline}</div>
                  </div>
                </div>
              )}
              {composition.children?.length > 0 && (
                <div className="composition-section">
                  <div className="composition-header"><Package size={16} /><span>Contains ({composition.children.length} items)</span></div>
                  <div className="composition-content">
                    {composition.children.map((child, idx) => (
                      <div key={idx} className="child-item">
                        <span className="child-icon">{child.type === 'pipeline' ? '📊' : '📓'}</span>
                        <span className="child-name">{child.name}</span>
                        <span className={`child-type ${child.type}`}>{child.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {composition.composition?.notebooks && (
                <div className="composition-section">
                  <div className="composition-header"><Activity size={16} /><span>DAG Structure ({composition.composition.notebooks.length} notebooks)</span></div>
                  <div className="composition-content">
                    <div className="notebook-list">
                      {composition.composition.notebooks.map((notebook, idx) => (
                        <span key={idx} className="notebook-badge">📓 {notebook}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {composition.composition && (
                <div className="composition-section">
                  <div className="composition-header"><Clock size={16} /><span>Pipeline Details</span></div>
                  <div className="composition-content">
                    <div className="pipeline-metadata">
                      {composition.composition.pipeline_type && (
                        <div className="metadata-item"><span className="metadata-key">Type:</span><span className="metadata-val">{composition.composition.pipeline_type}</span></div>
                      )}
                      {composition.composition.concurrency && (
                        <div className="metadata-item"><span className="metadata-key">Concurrency:</span><span className="metadata-val">{composition.composition.concurrency}</span></div>
                      )}
                      {composition.composition.timeout && (
                        <div className="metadata-item"><span className="metadata-key">Timeout:</span><span className="metadata-val">{composition.composition.timeout}s</span></div>
                      )}
                      {composition.lastUpdated && (
                        <div className="metadata-item"><span className="metadata-key">Last Updated:</span><span className="metadata-val">{formatDateTime(composition.lastUpdated)}</span></div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function formatTrialExpiry(isoString) {
  if (!isoString) return ''
  const diffDays = Math.ceil((new Date(isoString) - new Date()) / 86400000)
  if (diffDays < 0) return 'expired'
  if (diffDays === 0) return 'expires today'
  if (diffDays === 1) return 'expires tomorrow'
  return `expires in ${diffDays}d`
}

function formatDateTime(isoString) {
  if (!isoString) return 'Never'
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatScheduleDetails(details) {
  if (!details) return ''
  if (typeof details === 'string') return details
  const parts = []
  const scheduleType = details.type || details.frequency
  if (scheduleType) parts.push(scheduleType)
  if (details.startDateTime) {
    const time = new Date(details.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    parts.push(`at ${time}`)
  } else if (details.time) {
    parts.push(`at ${details.time}`)
  }
  if (details.days && Array.isArray(details.days)) {
    parts.push(`on ${details.days.join(', ')}`)
  } else if (details.day) {
    parts.push(`on ${details.day}`)
  }
  const tz = details.localTimeZoneId || details.timezone
  if (tz) parts.push(`(${tz.replace(' Standard Time', '').replace(' Daylight Time', '')})`)
  return parts.join(' ') || JSON.stringify(details)
}
